<script setup lang="ts">
import { useI18n } from 'vue-i18n'

/**
 * Branded loading indicator built from the SignMeUp mark: the check is drawn
 * stroke by stroke and the plus pops in, then the cycle repeats. The `3d` variant extrudes every
 * part into stacked slices and turns the mark in space. Reduced-motion users get a calm pulse.
 * With `intro`, the sequence plays once and settles on the finished logo.
 */
const props = withDefaults(
  defineProps<{
    /** Preset or any CSS length for the mark's width. */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | (string & {})
    variant?: 'flat' | '3d'
    /** Visible caption. Screen readers always get a label, falling back to "Loading...". */
    label?: string
    /** Play the build-up once and rest on the complete logo, facing the viewer. */
    intro?: boolean
  }>(),
  {
    size: 'md',
    variant: 'flat',
    label: undefined,
    intro: false,
  },
)

const { t } = useI18n()

const SIZES: Record<string, string> = {
  xs: '1rem',
  sm: '1.5rem',
  md: '2.5rem',
  lg: '4rem',
  xl: '6rem',
  '2xl': '9rem',
}

// Logo geometry in the source file's coordinate space (see assets/images/signmeup_logonly.svg).
const VIEW_BOX = '81.5 136.5 40.5 35.5'

type PartKey = 'top' | 'bottom' | 'check' | 'plus'

interface Part {
  key: PartKey
  d: string
  /** Solid shade for the extruded sides of the 3d variant. */
  side: string
  opacity?: number
}

const PARTS: Part[] = [
  {
    key: 'top',
    side: '#005f93',
    d: 'm 82.401936,147.1386 c 0.0168,-4.22625 0.0271,-4.69094 0.11208,-5.0513 0.616818,-2.6157 2.375184,-4.37787 4.980258,-4.99104 0.659626,-0.15526 22.406946,-0.21263 23.323686,-0.0615 0.64671,0.10659 1.8343,0.5038 1.83729,0.6145 4e-4,0.0184 -0.0765,0.0927 -0.17108,0.1652 -0.38444,0.29459 -1.90629,1.62099 -2.50911,2.18685 l -0.64381,0.60434 -10.28349,9.2e-4 c -11.394356,0.001 -10.667114,-0.0216 -11.381732,0.35358 -0.638906,0.33542 -1.110902,0.87857 -1.37808,1.58583 -0.114228,0.30237 -0.117126,0.37276 -0.143048,3.47243 l -0.02646,3.16381 -0.517598,0.20826 c -1.012158,0.40727 -1.93268,1.04071 -2.710312,1.86504 l -0.507084,0.53753 z',
  },
  {
    key: 'bottom',
    side: '#003c5c',
    d: 'm 82.414216,154.67915 -0.01344,5.3852 c -0.01507,5.92585 -0.01956,5.82891 0.311609,6.81199 0.832868,2.47239 2.957279,4.0844 5.687508,4.31498 0.360231,0.0304 5.840257,0.0319 11.298019,0.0181 0.03519,-9e-5 0.06248,9e-5 0.09767,0 v -5.2e-4 c 0.06148,-2.9e-4 0.121161,-5.2e-4 0.19017,-5.2e-4 5.312558,-0.0143 10.511788,-0.0431 10.736298,-0.0744 3.28031,-0.45693 5.43278,-2.97979 5.51542,-6.4642 l 0.0264,-1.09813 -0.416,0.0398 c -0.53005,0.0504 -2.71955,0.0517 -3.0427,0.002 l -0.24495,-0.0377 -0.0388,0.83147 c -0.0769,1.64948 -0.67175,2.53271 -2.03243,3.01842 -0.17339,0.0619 -5.27076,0.10051 -10.503238,0.1111 -0.10449,2e-4 -0.183258,0.001 -0.287838,0.001 v 5.1e-4 c -5.453711,0.01 -10.950616,-0.0126 -11.257711,-0.076 -1.018789,-0.20993 -1.858971,-0.97572 -2.17041,-1.97817 -0.09941,-0.32001 -0.104387,-0.50762 -0.104387,-3.87264 v -3.53621 L 85.0616,157.04541 c -0.607223,-0.56637 -1.451531,-1.33048 -1.875855,-1.69808 z',
  },
  {
    key: 'check',
    side: '#c25400',
    d: 'm 95.62916,165.02741 c -0.657608,-0.18508 -0.816212,-0.3211 -1.779942,-1.5264 -2.595598,-3.24623 -6.75772,-7.54306 -9.282716,-9.58317 -0.39513,-0.31925 -0.539152,-0.51463 -0.537108,-0.72865 0.0074,-0.77179 1.802852,-2.01425 3.024644,-2.09303 0.47847,-0.0309 0.406864,-0.0662 1.629158,0.80314 0.71018,0.50508 1.164476,0.8484 2.585734,1.95411 0.612424,0.47645 2.378654,1.91108 3.527638,2.86534 0.665818,0.55298 1.223994,0.99666 1.240392,0.98595 0.0164,-0.0107 0.541782,-0.61875 1.167522,-1.3512 2.03686,-2.38424 4.081408,-4.69592 5.503758,-6.22287 0.33888,-0.3638 1.15956,-1.24487 1.82373,-1.95792 3.63138,-3.89864 8.28674,-8.25523 11.07522,-10.36442 0.91116,-0.6892 1.68233,-0.64156 2.52564,0.156 0.94304,0.89189 0.87859,1.10583 -0.86764,2.87989 -1.67208,1.69873 -3.38182,3.52245 -4.6355,4.94452 -2.03017,2.30286 -2.29551,2.61011 -3.42896,3.97063 -0.64294,0.77174 -1.33891,1.60558 -1.54661,1.85296 -0.7802,0.92931 -3.32902,4.1633 -4.56673,5.79438 -2.19405,2.89133 -3.520234,4.68 -4.833118,6.51858 -0.685664,0.96021 -1.666412,1.37198 -2.625112,1.10216 z',
  },
  {
    key: 'plus',
    side: '#3b6f10',
    opacity: 0.97,
    d: 'm 112.78375,161.5664 c -0.40269,-0.24556 -0.39218,-0.18852 -0.39218,-2.12958 v -1.73911 l -1.70656,-8e-5 c -2.44264,-1.2e-4 -2.3152,0.10445 -2.31491,-1.89952 2e-4,-1.99062 -0.1626,-1.85748 2.27259,-1.85748 h 1.74888 l 2e-4,-1.73302 c 2e-4,-2.38828 -0.13017,-2.23584 1.91279,-2.23554 1.99042,2e-4 1.86572,-0.14668 1.88345,2.21991 l 0.0129,1.72219 1.82048,0.0265 c 2.39035,0.0347 2.20184,-0.1252 2.20184,1.86808 0,1.99137 0.12557,1.8888 -2.31242,1.88892 l -1.70388,8e-5 -0.0159,1.73302 c -0.018,1.95715 -0.0109,1.92254 -0.43388,2.1273 -0.32909,0.15931 -2.71485,0.16602 -2.97339,0.008 z',
  },
]

// Depth of each part in the 3d variant, in em of the mark's width: [z of the back slice, thickness].
const DEPTH: Record<PartKey, [number, number]> = {
  top: [0, 0.07],
  bottom: [0, 0.07],
  check: [0.1, 0.06],
  plus: [0.19, 0.06],
}

const uid = useId()
const gradientId = (key: PartKey) => `${uid}-${key}`

const markSize = computed(() => SIZES[props.size] ?? props.size)

// Small marks need fewer slices to read as solid; large ones need more to avoid visible banding.
const sliceCount = computed(() =>
  props.size === 'xs' || props.size === 'sm' ? 3 : props.size === 'md' ? 5 : 9,
)

function slicesFor(key: PartKey) {
  const [z, thickness] = DEPTH[key]
  const count = sliceCount.value
  return Array.from({ length: count }, (_, index) => ({
    z: z + (thickness * index) / (count - 1),
    front: index === count - 1,
  }))
}

const srLabel = computed(() => props.label ?? t('common.labels.loading'))
</script>

<template>
  <span
    role="status"
    aria-live="polite"
    class="app-loader inline-flex flex-col items-center gap-3"
    :class="[`app-loader--${variant}`, { 'app-loader--intro': intro }]"
  >
    <span class="app-loader__mark" :style="{ fontSize: markSize }" aria-hidden="true">
      <svg class="app-loader__defs" width="0" height="0" focusable="false">
        <defs>
          <linearGradient
            :id="gradientId('top')"
            x1="139.99043"
            y1="18.229387"
            x2="31.111856"
            y2="123.3722"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(0.2,0,0,0.2,80.409121,130.20955)"
          >
            <stop offset="0" stop-color="#11a8ff" />
            <stop offset="1" stop-color="#007dc1" />
          </linearGradient>
          <linearGradient
            :id="gradientId('bottom')"
            x1="82.395561"
            y1="162.94846"
            x2="116.26383"
            y2="162.94846"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stop-color="#007fba" />
            <stop offset="1" stop-color="#005d8e" />
          </linearGradient>
          <linearGradient
            :id="gradientId('check')"
            x1="20.325214"
            y1="124.55849"
            x2="189.42149"
            y2="85.519547"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(0.2,0,0,0.2,80.409121,130.20955)"
          >
            <stop offset="0" stop-color="#ffcc00" />
            <stop offset="1" stop-color="#ff7800" />
          </linearGradient>
          <linearGradient
            :id="gradientId('plus')"
            x1="149.00648"
            y1="107.11656"
            x2="190.93373"
            y2="149.04382"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(0.2,0,0,0.2,80.303288,130.20955)"
          >
            <stop offset="0" stop-color="#6ec71d" />
            <stop offset="1" stop-color="#529617" />
          </linearGradient>
        </defs>
      </svg>

      <svg v-if="variant === 'flat'" class="app-loader__svg" :viewBox="VIEW_BOX">
        <path
          v-for="part in PARTS"
          :key="part.key"
          :class="`app-loader__${part.key}`"
          :d="part.d"
          :fill="`url(#${gradientId(part.key)})`"
          :fill-opacity="part.opacity"
        />
      </svg>

      <span v-else class="app-loader__stage">
        <span class="app-loader__rig">
          <span
            v-for="part in PARTS"
            :key="part.key"
            class="app-loader__part"
            :class="`app-loader__part--${part.key}`"
          >
            <svg
              v-for="(slice, index) in slicesFor(part.key)"
              :key="index"
              class="app-loader__slice"
              :viewBox="VIEW_BOX"
              :style="{ transform: `translateZ(${slice.z}em)` }"
            >
              <path
                :class="`app-loader__${part.key}`"
                :d="part.d"
                :fill="slice.front ? `url(#${gradientId(part.key)})` : part.side"
                :fill-opacity="part.opacity"
              />
            </svg>
          </span>
        </span>
      </span>
    </span>

    <span v-if="label" class="text-center text-sm font-medium text-muted">{{ label }}</span>
    <span v-else class="sr-only">{{ srLabel }}</span>
  </span>
</template>

<style scoped>
.app-loader {
  --app-loader-cycle: 2.4s;
  line-height: 1;
}

.app-loader__mark {
  position: relative;
  display: block;
  width: 1em;
  height: calc(1em * 35.5 / 40.5);
}

.app-loader__defs {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}

.app-loader__svg,
.app-loader__slice {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* --- shared part choreography ------------------------------------------------------------- */

.app-loader__check {
  animation: app-loader-draw var(--app-loader-cycle) cubic-bezier(0.55, 0, 0.35, 1) infinite;
}

.app-loader__plus {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: app-loader-pop var(--app-loader-cycle) cubic-bezier(0.3, 0, 0.2, 1) infinite;
}

/* The check is drawn left to right: down-stroke first, then the long up-stroke. */
@keyframes app-loader-draw {
  0%,
  8% {
    clip-path: inset(0 100% 0 0);
    opacity: 1;
  }
  42%,
  80% {
    clip-path: inset(0 0 0 0);
    opacity: 1;
  }
  94%,
  100% {
    clip-path: inset(0 0 0 0);
    opacity: 0;
  }
}

/* The plus pops in once the check has landed. */
@keyframes app-loader-pop {
  0%,
  38% {
    transform: scale(0) rotate(-90deg);
    opacity: 0;
  }
  52% {
    transform: scale(1.18) rotate(0deg);
    opacity: 1;
  }
  60%,
  80% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  94%,
  100% {
    transform: scale(0.6) rotate(0deg);
    opacity: 0;
  }
}

/* --- 3d variant ---------------------------------------------------------------------------- */

.app-loader__stage {
  position: absolute;
  inset: 0;
  perspective: 4em;
}

.app-loader__rig,
.app-loader__part {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
}

.app-loader__rig {
  animation: app-loader-sway calc(var(--app-loader-cycle) * 2) ease-in-out infinite;
}

.app-loader__slice {
  position: absolute;
  inset: 0;
  backface-visibility: visible;
}

/* Check and plus float forward while being drawn, then sink back onto the frame. */
.app-loader__part--check,
.app-loader__part--plus {
  animation: app-loader-lift var(--app-loader-cycle) cubic-bezier(0.3, 0, 0.2, 1) infinite;
}

@keyframes app-loader-sway {
  0%,
  100% {
    transform: rotateX(14deg) rotateY(-32deg);
  }
  50% {
    transform: rotateX(8deg) rotateY(32deg);
  }
}

@keyframes app-loader-lift {
  0%,
  8% {
    transform: translateZ(0.14em);
  }
  42% {
    transform: translateZ(0.14em);
  }
  50%,
  100% {
    transform: translateZ(0);
  }
}

/* --- intro: play once, rest on the finished logo facing the viewer -------------------------- */

.app-loader--intro {
  --app-loader-intro: 1.6s;
}

.app-loader--intro .app-loader__check {
  animation: app-loader-draw-intro var(--app-loader-intro) cubic-bezier(0.55, 0, 0.35, 1) both;
}

.app-loader--intro .app-loader__plus {
  animation: app-loader-pop-intro var(--app-loader-intro) cubic-bezier(0.3, 0, 0.2, 1) both;
}

.app-loader--intro .app-loader__part--check,
.app-loader--intro .app-loader__part--plus {
  animation: app-loader-lift-intro var(--app-loader-intro) cubic-bezier(0.3, 0, 0.2, 1) both;
}

.app-loader--intro .app-loader__rig {
  animation: app-loader-face var(--app-loader-intro) cubic-bezier(0.25, 0.1, 0.25, 1) both;
}

@keyframes app-loader-draw-intro {
  0%,
  12% {
    clip-path: inset(0 100% 0 0);
  }
  62%,
  100% {
    clip-path: inset(0 0 0 0);
  }
}

@keyframes app-loader-pop-intro {
  0%,
  58% {
    transform: scale(0) rotate(-90deg);
    opacity: 0;
  }
  80% {
    transform: scale(1.18) rotate(0deg);
    opacity: 1;
  }
  92%,
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

@keyframes app-loader-lift-intro {
  0%,
  62% {
    transform: translateZ(0.14em);
  }
  76%,
  100% {
    transform: translateZ(0);
  }
}

@keyframes app-loader-face {
  0% {
    transform: rotateX(14deg) rotateY(-32deg);
  }
  100% {
    transform: rotateX(0deg) rotateY(0deg);
  }
}

/* --- reduced motion ------------------------------------------------------------------------ */

@media (prefers-reduced-motion: reduce) {
  .app-loader .app-loader__check,
  .app-loader .app-loader__plus,
  .app-loader .app-loader__part--plus,
  .app-loader .app-loader__part--check {
    animation: none;
  }

  .app-loader .app-loader__rig {
    animation: none;
    transform: rotateX(10deg) rotateY(-20deg);
  }

  .app-loader.app-loader--intro .app-loader__rig {
    transform: none;
  }

  .app-loader:not(.app-loader--intro) .app-loader__mark {
    animation: app-loader-breathe 1.6s ease-in-out infinite;
  }

  @keyframes app-loader-breathe {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
}
</style>
